import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Colors } from 'context';
import { CareerIcon } from 'atoms/CareerIcon/CareerIcon';
import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph';
import { useSetMySkillLevel } from 'core/hooks/CareerProfile/useSetMySkillLevel';
import { SkillLevelChoice } from 'molecules/SkillLevelChoice';
import { careerColor, CTA_TEXT } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

import { recommendedNode } from '../../model/skillMapExpansion';
import { ISkillGraphIndex } from '../../model/skillGraphModel';
import { requirementDisplay } from '../../model/skillLevelLabel';
import { COURSE_CTA_KEY, IPassportCourse } from '../../model/skillPassportCourses';
import { Block, Lead, Meta } from './passportStyles';
import { SkillPassportCourse } from './SkillPassportCourse';

interface ISkillPassportActions {
  index: ISkillGraphIndex;
  node: ICareerSkillNode;
  course: IPassportCourse | null;
  /** id навыка словаря — из детального запроса; `null`, пока он не пришёл. */
  skillId: string | null;
  onOpenSkill: (slug: string) => void;
  /**
   * Выбранный, но не записанный уровень. Живёт ВЫШЕ паспорта (на карте):
   * переключение блоков его не теряет, а закрытие паспорта с ним спрашивает
   * подтверждение.
   */
  draftLevel: number | null;
  onDraftChange: (level: number | null) => void;
}

type PanelKey = 'assess' | 'learn' | null;

/**
 * Два действия паспорта (ТЗ 0073, 4.2) вместо развёрнутой формы.
 *
 * * «Już to potrafię — oceń poziom» раскрывает шкалу и «Zapisz ocenę»;
 * * «Chcę się nauczyć» раскрывает учебную рекомендацию (или честное «материала
 *   нет»); у выполненного требования на её месте — переход к следующему навыку.
 *
 * Раскрыт максимум один блок. В исходном состоянии пять вариантов шкалы не
 * занимают панель, а путь к обучению не спрятан под формой самооценки.
 *
 * 🔴 Выбор уровня НЕ сохраняет его: запись — только кнопкой. Ошибка оставляет
 * выбранное значение для повтора. Подтверждённый школой уровень отсюда не
 * переоценивается — сервер его и не понизит, а кнопка обещала бы обратное.
 */
export const SkillPassportActions = memo(
  ({
    index,
    node,
    course,
    skillId,
    onOpenSkill,
    draftLevel,
    onDraftChange,
  }: ISkillPassportActions) => {
    const { t } = useTranslation();
    const { setLevel, loading } = useSetMySkillLevel();
    const display = requirementDisplay(node);
    const [panel, setPanel] = useState<PanelKey>(null);
    const [result, setResult] = useState<'saved' | 'error' | null>(null);

    // Другой навык в том же паспорте — блоки свёрнуты заново.
    useEffect(() => {
      setPanel(null);
      setResult(null);
    }, [node.id]);

    if (display === 'upcoming' && node.upcoming) {
      return (
        <Block aria-label={t('careerCenter.map.next.title')}>
          <Lead>
            {t('careerCenter.map.upcoming', {
              lesson: node.upcoming.lessonNumber,
              left: node.upcoming.lessonsLeft,
            })}
          </Lead>
        </Block>
      );
    }

    const canAssess = node.source !== 'course';
    const meets = display === 'meets';
    const nextNode = meets
      ? recommendedNode({
          ...index,
          nodesOf: new Map(
            Array.from(index.nodesOf.entries()).map(([code, rows]) => [
              code,
              rows.filter((row) => row.id !== node.id),
            ]),
          ),
        })
      : null;

    const missingBase =
      display === 'missing'
        ? (index.needsIn.get(node.id) || [])
            .filter((edge) => edge.kind === 'required')
            .map((edge) => index.byId.get(edge.from))
            .filter((row): row is ICareerSkillNode => Boolean(row))
            .filter((row) => row.requirementStatus !== 'meets')
        : [];

    const toggle = (next: Exclude<PanelKey, null>) => {
      setResult(null);
      setPanel((current) => (current === next ? null : next));
    };

    const choice = draftLevel ?? node.myLevel;

    const save = async () => {
      if (!skillId || !choice) return;
      const ok = await setLevel(skillId, choice);
      setResult(ok ? 'saved' : 'error');
      if (ok) {
        onDraftChange(null);
        setPanel(null);
      }
    };

    return (
      <Block aria-label={t('careerCenter.map.next.title')}>
        <Actions>
          {canAssess ? (
            <Toggle
              type="button"
              aria-expanded={panel === 'assess'}
              aria-controls="skm-passport-assess"
              $open={panel === 'assess'}
              onClick={() => toggle('assess')}>
              {t('careerCenter.map.actions.assess')}
              <Chevron $open={panel === 'assess'} aria-hidden>
                <CareerIcon name="chevronDown" size={16} />
              </Chevron>
            </Toggle>
          ) : null}

          {meets ? (
            nextNode ? (
              <Toggle type="button" $open={false} onClick={() => onOpenSkill(nextNode.slug)}>
                {t('careerCenter.map.nextSkill', { name: nextNode.name })}
              </Toggle>
            ) : null
          ) : (
            <Toggle
              type="button"
              aria-expanded={panel === 'learn'}
              aria-controls="skm-passport-learn"
              $open={panel === 'learn'}
              onClick={() => toggle('learn')}>
              {t('careerCenter.map.actions.learn')}
              <Chevron $open={panel === 'learn'} aria-hidden>
                <CareerIcon name="chevronDown" size={16} />
              </Chevron>
            </Toggle>
          )}
        </Actions>

        {meets && !nextNode ? <Meta>{t('careerCenter.map.allMet')}</Meta> : null}

        {/* Оба блока смонтированы и прячутся атрибутом: выбранный уровень
            переживает переключение, а `aria-controls` всегда указывает на
            существующий элемент. */}
        {canAssess ? (
          <Panel id="skm-passport-assess" hidden={panel !== 'assess'}>
            <Lead>{t('careerCenter.map.assess.lead', { required: node.requiredLevel })}</Lead>
            <Meta>{t('careerCenter.map.assess.selfNote')}</Meta>
            <SkillLevelChoice
              skillName={node.name}
              value={choice}
              onChange={(level) => {
                setResult(null);
                onDraftChange(level === node.myLevel ? null : level);
              }}
              variant="full"
              disabled={loading}
            />
            <PanelActions>
              <PrimaryButton
                type="button"
                onClick={save}
                disabled={!choice || !skillId || loading || choice === node.myLevel}
                aria-busy={loading}>
                {t('careerCenter.map.assess.save')}
              </PrimaryButton>
            </PanelActions>
            {result === 'error' ? (
              <ErrorText role="alert">{t('careerCenter.map.assess.error')}</ErrorText>
            ) : null}
          </Panel>
        ) : null}

        {!meets ? (
          <Panel id="skm-passport-learn" hidden={panel !== 'learn'}>
            <Lead>
              {missingBase.length
                ? t('careerCenter.map.next.prerequisites', {
                    names: missingBase.map((row) => row.name).join(', '),
                  })
                : display === 'improve'
                ? t('careerCenter.map.next.improve', {
                    mine: node.myLevel,
                    required: node.requiredLevel,
                  })
                : t('careerCenter.map.next.missing', { required: node.requiredLevel })}
            </Lead>
            {course ? (
              <>
                <PanelActions>
                  <PrimaryLink href={course.courseUrl} target="_blank" rel="noopener noreferrer">
                    {t(COURSE_CTA_KEY[course.access])}
                    <Hidden>{t('careerCenter.map.course.newTab')}</Hidden>
                  </PrimaryLink>
                  {course.access === 'available' ? (
                    <Paid>{t('careerCenter.map.course.paid')}</Paid>
                  ) : null}
                </PanelActions>
                <SkillPassportCourse course={course} showCta={false} inline />
              </>
            ) : (
              <Meta>{t('careerCenter.map.whereToGetEmpty')}</Meta>
            )}
          </Panel>
        ) : null}

        {result === 'saved' ? (
          <Meta role="status">{t('careerCenter.map.assess.saved')}</Meta>
        ) : null}
      </Block>
    );
  },
);

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const Toggle = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: ${careerLayout.hitTarget}px;
  padding: 8px 14px;
  border: 1px solid ${({ $open }) => ($open ? careerColor.text : careerColor.line)};
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  text-align: left;
  cursor: pointer;
  ${careerType.bodyStrong()};

  &:hover {
    background: ${careerColor.hover};
  }

  &:focus-visible {
    outline: 2px solid ${careerColor.text};
    outline-offset: 2px;
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex: none;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;

  /* display: flex перебивает атрибут hidden без явного правила. */
  &[hidden] {
    display: none;
  }
`;

const PanelActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
`;

const PrimaryButton = styled.button`
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 20px;
  border: 0;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${Colors.main};
  color: ${CTA_TEXT};
  cursor: pointer;
  text-align: left;
  ${careerType.bodyStrong()};

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const PrimaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  min-height: ${careerLayout.primaryButton}px;
  padding: 0 20px;
  border-radius: ${careerLayout.controlRadius}px;
  background: ${Colors.main};
  color: ${CTA_TEXT};
  text-decoration: none;
  ${careerType.bodyStrong()};

  &:hover {
    color: ${CTA_TEXT};
    background: ${Colors.main_dark};
  }
`;

const Paid = styled.span`
  padding: 2px 8px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${Colors.signal_red};
  ${careerType.meta()};
`;

const Hidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
